import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  PictureAsPdf,
  TableView,
  TrendingUp,
  AttachMoney,
  DirectionsCar,
  Assessment,
  Build,
} from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';

const formatCurrency = (value) => `R$ ${parseFloat(value || 0).toFixed(2)}`;

export default function Relatorios() {
  const [filtroRapido, setFiltroRapido] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [tipoRelatorio, setTipoRelatorio] = useState('vendas');
  const [relatorio, setRelatorio] = useState(null);
  const [loading, setLoading] = useState(false);

  const precisaPeriodo = tipoRelatorio !== 'estoque';
  const usaFiltrosCliente = tipoRelatorio === 'vendas' || tipoRelatorio === 'financeiro';

  const aplicarFiltroRapido = (dias) => {
    const hoje = new Date();
    const dataFimStr = hoje.toISOString().split('T')[0];
    const dataInicioDate = new Date();
    dataInicioDate.setDate(hoje.getDate() - dias);
    const dataInicioStr = dataInicioDate.toISOString().split('T')[0];
    setDataInicio(dataInicioStr);
    setDataFim(dataFimStr);
    setFiltroRapido(dias.toString());
  };

  const limparFiltros = () => {
    setDataInicio('');
    setDataFim('');
    setFiltroRapido('');
    setFiltroCidade('');
    setFiltroCliente('');
    setRelatorio(null);
  };

  const getParams = () => ({
    data_inicio: dataInicio,
    data_fim: dataFim,
    cidade: usaFiltrosCliente ? filtroCidade : '',
    cliente: usaFiltrosCliente ? filtroCliente : '',
  });

  const getEndpoint = (sufixo = '') => {
    switch (tipoRelatorio) {
      case 'vendas':
        return `/relatorios/vendas${sufixo}`;
      case 'estoque':
        return `/relatorios/estoque${sufixo}`;
      case 'financeiro':
        return `/relatorios/financeiro${sufixo}`;
      case 'manutencoes':
        return `/relatorios/manutencoes${sufixo}`;
      default:
        return `/relatorios/vendas${sufixo}`;
    }
  };

  const getResumoCards = () => {
    switch (tipoRelatorio) {
      case 'estoque':
        return [
          { label: 'Total em Estoque', value: relatorio.total_estoque || 0, color: 'primary', icon: <DirectionsCar sx={{ mr: 1, color: 'primary.main' }} /> },
          { label: 'Disponíveis', value: relatorio.disponiveis || 0, color: 'success', icon: <Assessment sx={{ mr: 1, color: 'success.main' }} /> },
          { label: 'Reservados', value: relatorio.reservados || 0, color: 'warning', icon: <Assessment sx={{ mr: 1, color: 'warning.main' }} /> },
          { label: 'Em Manutenção', value: relatorio.em_manutencao || 0, color: 'info', icon: <Build sx={{ mr: 1, color: 'info.main' }} /> },
        ];
      case 'financeiro':
        return [
          { label: 'Receita Total', value: formatCurrency(relatorio.receita_total), color: 'success', icon: <AttachMoney sx={{ mr: 1, color: 'success.main' }} /> },
          { label: 'Custo Total', value: formatCurrency(relatorio.custo_total), color: 'warning', icon: <TrendingUp sx={{ mr: 1, color: 'warning.main' }} /> },
          { label: 'Lucro Líquido', value: formatCurrency(relatorio.lucro_liquido), color: 'primary', icon: <Assessment sx={{ mr: 1, color: 'primary.main' }} /> },
          { label: 'ROI', value: `${parseFloat(relatorio.roi || 0).toFixed(2)}%`, color: 'secondary', icon: <DirectionsCar sx={{ mr: 1, color: 'secondary.main' }} /> },
        ];
      case 'manutencoes':
        return [
          { label: 'Total de Manutenções', value: relatorio.total_manutencoes || 0, color: 'primary', icon: <Build sx={{ mr: 1, color: 'primary.main' }} /> },
          { label: 'Gasto Total', value: formatCurrency(relatorio.valor_total), color: 'warning', icon: <AttachMoney sx={{ mr: 1, color: 'warning.main' }} /> },
          { label: 'Ticket Médio', value: formatCurrency(relatorio.ticket_medio), color: 'success', icon: <TrendingUp sx={{ mr: 1, color: 'success.main' }} /> },
          { label: 'Veículos Atendidos', value: relatorio.total_veiculos || 0, color: 'secondary', icon: <DirectionsCar sx={{ mr: 1, color: 'secondary.main' }} /> },
        ];
      case 'vendas':
      default:
        return [
          { label: 'Total de Vendas', value: relatorio.total_vendas || 0, color: 'primary', icon: <Assessment sx={{ mr: 1, color: 'primary.main' }} /> },
          { label: 'Valor Total', value: formatCurrency(relatorio.valor_total), color: 'success', icon: <AttachMoney sx={{ mr: 1, color: 'success.main' }} /> },
          { label: 'Lucro Total', value: formatCurrency(relatorio.lucro_total), color: 'warning', icon: <TrendingUp sx={{ mr: 1, color: 'warning.main' }} /> },
          { label: 'Margem Média', value: `${parseFloat(relatorio.margem_media || 0).toFixed(2)}%`, color: 'secondary', icon: <DirectionsCar sx={{ mr: 1, color: 'secondary.main' }} /> },
        ];
    }
  };

  const gerarRelatorio = async () => {
    try {
      if (precisaPeriodo && (!dataInicio || !dataFim)) {
        toast.error('Selecione o período do relatório');
        return;
      }

      setLoading(true);
      const response = await api.get(getEndpoint(), { params: getParams() });
      setRelatorio(response.data);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error(error.response?.data?.error || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const baixarArquivo = async (tipoArquivo) => {
    try {
      if (!relatorio) {
        toast.error('Gere um relatório primeiro');
        return;
      }

      const response = await api.get(getEndpoint(tipoArquivo === 'pdf' ? '/pdf' : '/excel'), {
        params: getParams(),
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: tipoArquivo === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-${tipoRelatorio}-${dataInicio || 'sem-periodo'}-${dataFim || 'sem-periodo'}.${tipoArquivo === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${tipoArquivo === 'pdf' ? 'PDF' : 'Excel'} baixado com sucesso!`);
    } catch (error) {
      console.error(`Erro ao baixar ${tipoArquivo}:`, error);
      toast.error(`Erro ao baixar ${tipoArquivo === 'pdf' ? 'PDF' : 'Excel'}`);
    }
  };

  const resumoCards = relatorio ? getResumoCards() : [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Relatórios e Análises
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>

        {precisaPeriodo && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom color="textSecondary">
                Períodos rápidos:
              </Typography>
              <ButtonGroup variant="outlined" size="small">
                <Button onClick={() => aplicarFiltroRapido(7)} variant={filtroRapido === '7' ? 'contained' : 'outlined'}>
                  Últimos 7 dias
                </Button>
                <Button onClick={() => aplicarFiltroRapido(15)} variant={filtroRapido === '15' ? 'contained' : 'outlined'}>
                  Últimos 15 dias
                </Button>
                <Button onClick={() => aplicarFiltroRapido(30)} variant={filtroRapido === '30' ? 'contained' : 'outlined'}>
                  Últimos 30 dias
                </Button>
                <Button onClick={() => aplicarFiltroRapido(90)} variant={filtroRapido === '90' ? 'contained' : 'outlined'}>
                  Últimos 90 dias
                </Button>
              </ButtonGroup>
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Grid container spacing={2} alignItems="center">
          {precisaPeriodo && (
            <>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Data Início"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => { setDataInicio(e.target.value); setFiltroRapido(''); }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Data Fim"
                  type="date"
                  value={dataFim}
                  onChange={(e) => { setDataFim(e.target.value); setFiltroRapido(''); }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          {usaFiltrosCliente && (
            <>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Cidade" value={filtroCidade} onChange={(e) => setFiltroCidade(e.target.value)} />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Cliente" value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)} />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Tipo de Relatório"
              value={tipoRelatorio}
              onChange={(e) => setTipoRelatorio(e.target.value)}
            >
              <MenuItem value="vendas">Vendas</MenuItem>
              <MenuItem value="estoque">Estoque</MenuItem>
              <MenuItem value="financeiro">Financeiro</MenuItem>
              <MenuItem value="manutencoes">Manutenções</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={gerarRelatorio}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
              {(dataInicio || dataFim || filtroCidade || filtroCliente) && (
                <Button variant="outlined" onClick={limparFiltros} sx={{ height: '56px' }}>
                  Limpar
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {relatorio && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {resumoCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                <Card sx={{ background: 'linear-gradient(135deg, rgba(214,40,40,0.12), rgba(0,0,0,0.4))', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {card.icon}
                      <Typography variant="subtitle2" color="textSecondary">{card.label}</Typography>
                    </Box>
                    <Typography variant="h4" color={`${card.color}.main`}>{card.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6">
                Relatório de {tipoRelatorio.charAt(0).toUpperCase() + tipoRelatorio.slice(1)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="contained" color="error" startIcon={<PictureAsPdf />} onClick={() => baixarArquivo('pdf')}>
                  Baixar PDF
                </Button>
                <Button variant="contained" color="success" startIcon={<TableView />} onClick={() => baixarArquivo('excel')}>
                  Baixar Excel
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {precisaPeriodo && dataInicio && dataFim && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Período:</strong> {new Date(dataInicio).toLocaleDateString('pt-BR')} até{' '}
                  {new Date(dataFim).toLocaleDateString('pt-BR')}
                </Typography>
              </Box>
            )}

            {relatorio.detalhes && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom><strong>Detalhes:</strong></Typography>
                <Grid container spacing={2}>
                  {Object.entries(relatorio.detalhes).map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          {key.replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                        <Typography variant="h6">{value}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </>
      )}

      {!relatorio && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Assessment sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Nenhum relatório gerado
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Ajuste os filtros e clique em "Gerar Relatório" para visualizar os dados
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
